"use client";

import { useState, useEffect, Suspense } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useSearchParams } from "next/navigation";
import { ShareToolbar } from "./ShareToolbar";
import { getCalculator } from "@/lib/calculators-data";
import { CALCULATOR_INTROS } from "@/lib/calculator-intros-data";
import { JsonLd, generateHowToSchema } from "./JsonLd";
import { CALCULATOR_HOWTOS } from "@/lib/calculator-howto-data";

interface CalculatorShellProps {
  name: string;
  description: string;
  icon?: string;
  category: "diy" | "high-value";
  accentColor?: string;
  inputsPanel: React.ReactNode;
  resultsPanel: React.ReactNode;
  onCalculate: () => void;
  hasResult: boolean;
  disclaimer?: string;
  proTip?: string;
  children?: React.ReactNode;
  introContent?: React.ReactNode;
}

const getGhostLabels = (name: string): string[] => {
  const n = name.toLowerCase();
  if (n.includes("concrete")) {
    return ["Total Volume", "Bags Required (80 lb)", "Material Est. Cost"];
  }
  if (n.includes("paint")) {
    return ["Paint Volume Needed", "Number of Gallons", "Material Est. Cost"];
  }
  if (n.includes("flooring")) {
    return ["Flooring Area Needed", "Estimated Boxes", "Material Est. Cost"];
  }
  if (n.includes("area")) {
    return ["Square Footage (Sq Ft)", "Square Yardage (Sq Yd)", "Square Meters (Sq M)"];
  }
  if (n.includes("fence")) {
    return ["Pickets Needed", "Posts Required", "Rails Required"];
  }
  if (n.includes("mulch")) {
    return ["Total Volume Needed", "Bags Required (2 cu ft)", "Material Est. Cost"];
  }
  if (n.includes("drywall")) {
    return ["Drywall Sheets (4x8)", "Drywall Sheets (4x12)", "Joint Compound Bags"];
  }
  if (n.includes("tile")) {
    return ["Total Tile Area", "Tiles Required", "Grout Bags Required"];
  }
  return ["Total Material Needed", "Packaged Quantity", "Est. Project Cost"];
};

function CalculatorShellContent({
  name,
  description,
  category,
  inputsPanel,
  resultsPanel,
  onCalculate,
  hasResult,
  disclaimer,
  proTip,
  children,
  introContent,
}: CalculatorShellProps) {
  const [showEmbed, setShowEmbed] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isEmbedParam = searchParams ? searchParams.get("embed") === "true" : false;
  const [isEmbedded, setIsEmbedded] = useState(isEmbedParam);

  useEffect(() => {
    if (typeof window !== "undefined") {
      let isInsideIframe = false;
      try {
        isInsideIframe = window.self !== window.top;
      } catch (e) {
        isInsideIframe = true;
      }
      if (isEmbedParam || isInsideIframe) {
        setIsEmbedded(true);
        document.body.classList.add("embed-mode");
        document.documentElement.classList.add("embed-mode");
      }
    }
  }, [isEmbedParam]);

  const slug = pathname ? pathname.split("/").filter(Boolean).pop() : "";
  const imageUrl = slug ? (category === "diy" ? `/images/${slug}_material_guide.webp` : `/images/${slug}_buying_guide.webp`) : "";
  const calc = slug ? getCalculator(slug) : undefined;
  const lastModified = calc?.lastModified;

  const defaultIntro = slug ? CALCULATOR_INTROS[slug] : undefined;
  const activeIntro = introContent !== undefined ? introContent : defaultIntro;

  const cleanName = name
    .replace(/Calculator/gi, "")
    .replace(/Cost/gi, "")
    .replace(/&/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.meazora.com";
  const absoluteImageUrl = imageUrl ? `${siteUrl}${imageUrl}` : "";
  
  const sanitizedPath = pathname ? (pathname.endsWith("/") ? pathname : `${pathname}/`) : "/";
  const currentUrl = `${siteUrl}${sanitizedPath}`;

  const defaultHowTo = slug ? CALCULATOR_HOWTOS[slug] : undefined;
  const howToSchema = defaultHowTo
    ? generateHowToSchema(defaultHowTo.name, defaultHowTo.description, currentUrl, defaultHowTo.steps)
    : undefined;

  const CALCULATOR_TO_PILLAR_MAP: Record<string, { title: string; slug: string }> = {
    concrete: { title: "Complete Concrete & Hardscaping Guide 2026", slug: "complete-concrete-guide" },
    driveway: { title: "Complete Concrete & Hardscaping Guide 2026", slug: "complete-concrete-guide" },
    "retaining-wall": { title: "Complete Concrete & Hardscaping Guide 2026", slug: "complete-concrete-guide" },
    mulch: { title: "Complete Concrete & Hardscaping Guide 2026", slug: "complete-concrete-guide" },
    area: { title: "Complete Concrete & Hardscaping Guide 2026", slug: "complete-concrete-guide" },
    paint: { title: "Home Painting Guide: Interior & Exterior", slug: "complete-painting-guide" },
    flooring: { title: "Flooring Installation & Cost Guide 2026", slug: "complete-flooring-guide" },
    tile: { title: "Flooring Installation & Cost Guide 2026", slug: "complete-flooring-guide" },
    countertop: { title: "Flooring Installation & Cost Guide 2026", slug: "complete-flooring-guide" },
    hvac: { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    solar: { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    insulation: { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    "water-heater": { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    "backup-generator": { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    "ev-charger": { title: "HVAC System Sizing & Cost Guide", slug: "complete-hvac-guide" },
    kitchen: { title: "Kitchen Remodel Cost & Planning Guide 2026", slug: "complete-kitchen-remodel-guide" },
    "cabinet-refacing": { title: "Kitchen Remodel Cost & Planning Guide 2026", slug: "complete-kitchen-remodel-guide" },
    bathroom: { title: "Bathroom Renovation Complete Guide 2026", slug: "complete-bathroom-guide" },
    roofing: { title: "Roof Replacement Guide: Costs, Materials & Lifespan", slug: "complete-roofing-guide" },
    "roof-shingles": { title: "Roof Replacement Guide: Costs, Materials & Lifespan", slug: "complete-roofing-guide" },
  };

  const pillar = slug ? CALCULATOR_TO_PILLAR_MAP[slug] : undefined;

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)] transition-colors duration-150">
      {howToSchema && <JsonLd data={howToSchema} />}

      {/* Header */}
      <div className="bg-slate-900 dark:bg-[#0c1424] text-slate-100 py-10 relative overflow-hidden bg-[radial-gradient(circle_at_85%_-20%,rgba(96,165,250,0.15),transparent_55%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[10.5px] font-mono tracking-wider mb-6 text-slate-400 uppercase select-none">
            <Link href="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-slate-600">/</span>
            <Link href="/calculators/" className="hover:text-white transition-colors">Calculators</Link>
            <span className="text-slate-600">/</span>
            <span className="text-slate-350 font-semibold" aria-current="page">{name}</span>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8 flex flex-col gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-wide uppercase font-heading-condensed text-white">
                  {name}
                </h1>
                <span className={category === "diy" ? "badge-diy" : "badge-value"}>
                  {category === "diy" ? "DIY Material" : "Cost Estimator"}
                </span>
              </div>
              <p className="text-sm text-slate-300 dark:text-slate-400 max-w-2xl leading-relaxed mt-2 font-sans">
                {description}
              </p>
            </div>
            {imageUrl && (
              <div className="md:col-span-4 hidden md:block select-none">
                <div className="relative rounded-lg overflow-hidden border border-slate-700 aspect-[15/10] w-full max-w-[280px] ml-auto shadow-lg bg-slate-950/50">
                  <Image
                    src={imageUrl}
                    alt={`${name} preview`}
                    width={300}
                    height={200}
                    priority
                    className="object-cover w-full h-full opacity-80 hover:opacity-100 transition-opacity duration-150"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="ticks" />

      {/* Main layout */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Share & Privacy Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[var(--line)] pb-4">
          <div className="text-xs text-[var(--ink-soft)] font-medium font-sans flex flex-wrap items-center gap-1.5">
            <span>Perform calculations locally and securely. No data leaves your device.</span>
            {lastModified && (
              <>
                <span className="text-[var(--line-strong)] font-light select-none hidden sm:inline">•</span>
                <span className="text-[var(--accent)] font-semibold">Updated: {lastModified}</span>
              </>
            )}
          </div>
          <ShareToolbar title={name} />
        </div>

        {activeIntro && (
          <div className="calculator-intro text-xs md:text-sm text-[var(--ink-soft)] leading-relaxed w-full font-sans">
            {typeof activeIntro === "string" ? (
              activeIntro.split("\n\n").map((para, idx) => (
                <p key={idx} className={idx > 0 ? "mt-3" : ""}>
                  {para}
                </p>
              ))
            ) : (
              activeIntro
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start w-full">
          
          {/* LEFT — Inputs */}
          <div className="lg:col-span-6 card-blueprint p-[28px] transition-colors">
            <h2 className="font-mono text-[11px] tracking-[0.1em] text-[var(--ink-faint)] uppercase border-b border-[var(--line)] pb-3 mb-4 select-none">
              Enter Your {cleanName} Project Dimensions
            </h2>

            <div className="space-y-[18px]">
              {inputsPanel}
            </div>

            <button
              id={`btn-calculate-${name.toLowerCase().replace(/\s+/g, "-")}`}
              className="btn-primary mt-[18px]"
              onClick={onCalculate}
            >
              Calculate &rarr;
            </button>
          </div>

          {/* RIGHT — Results */}
          <div className="lg:col-span-6">
            <div className="card-blueprint p-[28px] flex flex-col justify-between gap-6 h-full min-h-[360px]">
              <div className="flex-1">
                <h2 className="font-mono text-[11px] tracking-[0.1em] text-[var(--ink-faint)] uppercase border-b border-[var(--line)] pb-3 mb-4 select-none">
                  Your {cleanName} Estimate
                </h2>
                {!hasResult ? (
                  <div>
                    <noscript>
                      <div className="mb-6 p-4 rounded bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-300 border border-amber-200 dark:border-amber-900 text-xs font-semibold leading-relaxed">
                        JavaScript is required to perform calculations and view results on this page. Please enable JavaScript in your browser settings.
                      </div>
                    </noscript>
                    {getGhostLabels(name).map((lbl, idx) => (
                      <div key={idx} className="ghost-line animate-pulse">
                        <span className="ghost-line-label">{lbl}</span>
                        <span className="ghost-line-value">— —</span>
                      </div>
                    ))}
                    <div className="results-empty-note mt-6">
                      Complete measurements and press calculate &rarr;
                    </div>
                  </div>
                ) : (
                  <div>
                    {resultsPanel}
                  </div>
                )}
              </div>

              <div className="space-y-4 pt-4 border-t border-[var(--line)] mt-auto">
                {/* Topic Hub Guide Link */}
                {pillar && (
                  <div className="p-3 bg-[var(--surface-muted)] border border-[var(--line)] rounded-lg text-xs leading-normal flex flex-col gap-1 font-sans">
                    <span className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)]">
                      Master Hub Guide
                    </span>
                    <p className="text-[11.5px] text-[var(--ink-soft)] font-medium m-0">
                      Learn building standards, code requirements, and material types in our{" "}
                      <Link
                        href={`/articles/${pillar.slug}/`}
                        className="font-bold text-[var(--accent)] hover:underline"
                      >
                        {pillar.title} &rarr;
                      </Link>
                    </p>
                  </div>
                )}

                {/* Disclaimer */}
                {/* Pro Tip first (only for diy) */}
                {category === "diy" && proTip && (
                  <div className="info-box text-xs font-medium">
                    <strong>Pro Tip:</strong> {proTip}
                  </div>
                )}

                {/* Disclaimer second (smaller/discreet note) */}
                {disclaimer && (
                  <div className="disclaimer-box text-[11px] font-medium text-[var(--ink-faint)] leading-relaxed">
                    <strong>Note:</strong> {disclaimer}
                  </div>
                )}

                {/* Embed This Calculator Widget */}
                <div className="border-t border-[var(--line)] pt-3 mt-2">
                  <button
                    onClick={() => setShowEmbed(!showEmbed)}
                    className="flex items-center justify-between w-full py-1 text-left text-[11px] font-mono font-bold uppercase tracking-wider text-[var(--ink-soft)] hover:text-[var(--accent)] transition-colors cursor-pointer"
                  >
                    <span>🔌 Embed This Calculator</span>
                    <span>{showEmbed ? "▲" : "▼"}</span>
                  </button>
                  {showEmbed && (
                    <div className="mt-2 p-3 rounded bg-[var(--surface-muted)] border border-[var(--border)] flex flex-col gap-2">
                      <p className="text-[10px] text-[var(--ink-soft)] leading-normal m-0">
                        Add this interactive tool to your blog or page. Copy and paste the HTML code below:
                      </p>
                      <textarea
                        readOnly
                        className="w-full p-2 bg-[var(--surface)] text-[var(--ink-soft)] rounded border border-[var(--border)] h-16 resize-none font-mono text-[9px] focus:outline-none select-all"
                        onClick={(e) => (e.target as HTMLTextAreaElement).select()}
                        value={`<iframe src="https://www.meazora.com/calculators/${slug}?embed=true" width="100%" height="650" frameborder="0"></iframe>\n<p style="font-size:11px;text-align:center;">Calculator powered by <a href="https://www.meazora.com/calculators/${slug}">Meazora ${name}</a></p>`}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {children && (
          <div className="border-t border-[var(--line-strong)] pt-10">
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CalculatorShell(props: CalculatorShellProps) {
  return (
    <Suspense fallback={<div className="min-h-screen bg-[var(--background)] animate-pulse" />}>
      <CalculatorShellContent {...props} />
    </Suspense>
  );
}
